import React from 'react';
import styles from '../styles/Skeleton.module.css';

/**
 * Base skeleton element
 */
export const Skeleton = ({ 
  width, 
  height, 
  borderRadius, 
  className = '', 
  style = {},
  dark = false 
}) => {
  return (
    <div
      className={`${dark ? styles.skeletonDark : styles.skeleton} ${className}`}
      style={{
        width,
        height,
        borderRadius,
        ...style
      }}
    />
  );
};

/**
 * Text line skeleton
 */
export const SkeletonText = ({ lines = 1, width, className = '' }) => {
  return (
    <div className={className}>
      {Array.from({ length: lines }).map((_, i) => (
        <div 
          key={i} 
          className={styles.skeletonText}
          style={width ? { width: typeof width === 'number' ? `${width}%` : width } : undefined}
        />
      ))}
    </div>
  );
};

/**
 * Circle skeleton (avatars, icons)
 */
export const SkeletonCircle = ({ size = 40, className = '' }) => {
  return (
    <div
      className={`${styles.skeletonCircle} ${className}`}
      style={{ width: size, height: size }}
    />
  );
};

/**
 * Generic loading container with spinner
 */
export const LoadingSpinner = ({ text = 'Caricamento…', className = '' }) => {
  return (
    <div className={`${styles.loadingContainer} ${className}`}>
      <div className={styles.loadingSpinner} />
      {text && <div className={styles.loadingText}>{text}</div>}
    </div>
  );
};

/**
 * Inline button loading indicator (spinner)
 */
export const ButtonSpinner = ({ dark = false }) => {
  return <span className={dark ? styles.buttonSpinnerDark : styles.buttonSpinner} />;
};

/**
 * Inline button loading indicator (dots)
 */
export const ButtonDots = () => {
  return (
    <span className={styles.buttonDots}>
      <span className={styles.buttonDot} />
      <span className={styles.buttonDot} />
      <span className={styles.buttonDot} />
    </span>
  );
};

/**
 * Button content with loading state
 */
export const ButtonLoading = ({ loading, loadingText, children, useDots = false }) => {
  if (!loading) return children;
  
  return (
    <span className={styles.buttonLoading}>
      {loadingText || children}
      {useDots ? <ButtonDots /> : <ButtonSpinner />}
    </span>
  );
};

/* ============================================
   GAME-SPECIFIC SKELETONS
   ============================================ */

/**
 * Collegamenti game skeleton - matches actual 4x4 word grid
 */
export const CollegamentiSkeleton = () => {
  return (
    <div className={styles.colleamentiContainer}>
      <div className={styles.colleamentiGrid}>
        {Array.from({ length: 16 }).map((_, i) => (
          <div key={i} className={styles.colleamentiCell} />
        ))}
      </div>
      <div className={styles.colleamentiLives}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={styles.colleamentiLife} />
        ))}
      </div>
      <div className={styles.colleamentiButtons}>
        <div className={styles.colleamentiButton} />
        <div className={styles.colleamentiButton} />
        <div className={styles.colleamentiButton} />
      </div>
    </div>
  );
};

/**
 * Cloze game skeleton - matches actual text card and word bank layout
 */
export const ClozeSkeleton = () => {
  // Varying widths for word tokens to look more natural
  const wordWidths = [85, 72, 95, 78, 88, 72];
  
  return (
    <div className={styles.clozeContainer}>
      <div className={styles.clozeCard}>
        <div className={styles.clozeTextLine} style={{ width: '95%' }} />
        <div className={styles.clozeTextLine} style={{ width: '88%' }} />
        <div className={styles.clozeTextLine} style={{ width: '92%' }} />
        <div className={styles.clozeTextLine} style={{ width: '78%' }} />
        <div className={styles.clozeTextLine} style={{ width: '85%' }} />
        <div className={styles.clozeTextLine} style={{ width: '60%' }} />
      </div>
      <div className={styles.clozeWordBank}>
        {wordWidths.map((width, i) => (
          <div key={i} className={styles.clozeWord} style={{ width: `${width}px` }} />
        ))}
      </div>
      <div className={styles.clozeLives}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={styles.clozeLife} />
        ))}
      </div>
    </div>
  );
};

/**
 * Memoria game skeleton - matches actual 3x4 card grid (12 cards)
 */
export const MemoriaSkeleton = () => {
  return (
    <div className={styles.memoriaContainer}>
      <div className={styles.memoriaGrid}>
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className={styles.memoriaCard} />
        ))}
      </div>
      <div className={styles.memoriaLives}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={styles.memoriaLife} />
        ))}
      </div>
    </div>
  );
};

/**
 * Ortografia game skeleton - matches actual hexagon honeycomb layout
 */
export const OrtografiaSkeleton = () => {
  return (
    <div className={styles.ortografiaContainer}>
      <div className={styles.ortografiaProgress} />
      <div className={styles.ortografiaWords} />
      <div className={styles.ortografiaInput} />
      <div className={styles.ortografiaHexGrid}>
        <div className={styles.ortografiaHexRow}>
          <div className={styles.ortografiaHex} />
          <div className={styles.ortografiaHex} />
        </div>
        <div className={styles.ortografiaHexRow}>
          <div className={styles.ortografiaHex} />
          <div className={styles.ortografiaHex} />
          <div className={styles.ortografiaHex} />
        </div>
        <div className={styles.ortografiaHexRow}>
          <div className={styles.ortografiaHex} />
          <div className={styles.ortografiaHex} />
        </div>
      </div>
      <div className={styles.ortografiaButtons}>
        <div className={styles.ortografiaButton} />
        <div className={styles.ortografiaButton} />
      </div>
    </div>
  );
};

/**
 * Scalinata game skeleton - matches actual 5-level word ladder staircase pattern
 * Uses 8-column grid (same as actual game) so shorter words create staircase effect
 */
export const ScalinataSkeleton = () => {
  return (
    <div className={styles.scalinataContainer}>
      <div className={styles.scalinataLevels}>
        {/* 5 levels: 4, 5, 6, 7, 8 letters - using 8-column grid creates staircase */}
        {[4, 5, 6, 7, 8].map((letterCount, levelIdx) => (
          <div key={levelIdx} className={styles.scalinataLevel}>
            {Array.from({ length: letterCount }).map((_, i) => (
              <div key={i} className={styles.scalinataLetter} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Spettri game skeleton - matches actual two-column spectrum layout (5 items each)
 */
export const SpettriSkeleton = () => {
  return (
    <div className={styles.spettriContainer}>
      <div className={styles.spettriColumns}>
        <div className={styles.spettriWrapper}>
          <div className={styles.spettriColumn}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={styles.spettriItem} />
            ))}
          </div>
        </div>
        <div className={styles.spettriWrapper}>
          <div className={styles.spettriColumn}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={styles.spettriItem} />
            ))}
          </div>
        </div>
      </div>
      <div className={styles.spettriLives}>
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={styles.spettriLife} />
        ))}
      </div>
      <div className={styles.spettriButton} />
    </div>
  );
};

/**
 * Serpentoni game skeleton - matches 8x6 letter board and bottom controls.
 */
export const SerpentoniSkeleton = () => {
  return (
    <div className={styles.serpentoniContainer}>
      <div className={styles.serpentoniPrompt} />
      <div className={styles.serpentoniBoard}>
        {Array.from({ length: 48 }).map((_, i) => (
          <div key={i} className={styles.serpentoniCell} />
        ))}
      </div>
      <div className={styles.serpentoniStats}>
        <div className={styles.serpentoniPill} />
        <div className={styles.serpentoniPill} />
        <div className={styles.serpentoniPill} />
      </div>
      <div className={styles.serpentoniButtons}>
        <div className={styles.serpentoniButton} />
        <div className={styles.serpentoniButton} />
      </div>
    </div>
  );
};

/**
 * Generic game skeleton - picks the right one based on game ID
 */
export const GameSkeleton = ({ gameId }) => {
  switch (gameId) {
    case 'collegamenti':
      return <CollegamentiSkeleton />;
    case 'cloze':
      return <ClozeSkeleton />;
    case 'memoria':
      return <MemoriaSkeleton />;
    case 'ortografia':
      return <OrtografiaSkeleton />;
    case 'scalinata':
      return <ScalinataSkeleton />;
    case 'spettri':
      return <SpettriSkeleton />;
    case 'serpentoni':
      return <SerpentoniSkeleton />;
    default:
      return <LoadingSpinner />;
  }
};

export default Skeleton;
